<template>
  <div class="w-full h-full flex flex-col bg-cw-900">
    <div class="bg-white flex items-center justify-center py-8">
      <img class="w-8/12" src="/img/cw-logo.png" alt="CareerWise icon" />
    </div>
    <div class="h-3 mb-4 w-full cw-gradient"></div>
    <nav class="flex flex-col">
      <OrganisationSelector v-if="isSuperAdmin" />

      <div
        v-for="(item, i) in menuItems"
        :key="i"
        class="space-x-3 flex pl-6 py-2 text-white cursor-pointer hover:bg-cw-700 items-center"
        :class="isActive(item) ? 'bg-cw-700' : ''"
        @click="triggerManagerMenuItem(item)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="item.svgPath1"
          />
          <path
            v-if="item.svgPath2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :d="item.svgPath2"
          />
        </svg>
        <p class="text-lg">{{ item.name }}</p>
      </div>
    </nav>
    <div
      class="mt-auto cw-gradient flex items-center justify-between px-6 py-3"
    >
      <div
        :class="[
          'flex items-center justify-center space-x-3 text-white',
          user?.name ? 'visible' : 'invisible',
        ]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 flex-shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <p class="font-light truncate overflow-ellipsis w-10/12">
          {{ user?.name }}
        </p>
      </div>
      <div title="Logout">
        <div v-if="isLoggingOut" class="w-5 h-5">
          <lottie :options="loadingAnimationOptions" />
        </div>
        <svg
          v-else-if="user"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 flex-shrink-0 cursor-pointer text-white hover:text-cw-900"
          @click="signOut"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import OrganisationSelector from './OrganisationSelector.vue'

export default defineComponent({
  name: 'Menu',

  components: {
    OrganisationSelector,
  },

  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const user = computed(() => store.getters['shared/getUser'])
    const isSuperAdmin = computed(() => store.getters['shared/isSuperAdmin'])
    const loadingAnimationOptions = { animationData: loadingAnimation.default }

    const isContributorAdminConnectionsOnly = computed(() => {
      return store.getters['shared/isContributorAdminConnectionsOnly']
    })

    const menuItems = computed(() => {
      const connectionsItem = {
        name: isContributorAdminConnectionsOnly.value
          ? 'My Connection'
          : 'Connections',
        matchedRoutes: [
          'ContributorsConnections',
          'ContributorsAddConnection',
          'ContributorsUpdateConnection',
        ],

        svgPath1:
          'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z',
      }

      if (!user.value) {
        return []
      }

      if (isContributorAdminConnectionsOnly.value) {
        return [connectionsItem]
      }

      return [
        {
          name: 'Dashboard',
          matchedRoutes: ['ContributorsDashboard'],
          svgPath1:
            'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
        },
        {
          name: 'Posts',
          matchedRoutes: ['ContributorsPosts', 'ContributorsPost'],
          svgPath1:
            'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
        },
        {
          name: 'Drafts',
          matchedRoutes: ['ContributorsDrafts'],
          svgPath1:
            'm16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10',
        },
        connectionsItem,
        {
          name: 'Settings',
          matchedRoutes: ['ContributorsSettings'],
          svgPath1:
            'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z',
          svgPath2: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
        },
        {
          name: 'Accounts',
          matchedRoutes: ['ContributorsAccounts'],
          svgPath1:
            'M16.5 12C16.8283 12 17.1534 11.9353 17.4567 11.8097C17.76 11.6841 18.0356 11.4999 18.2678 11.2678C18.4999 11.0356 18.6841 10.76 18.8097 10.4567C18.9353 10.1534 19 9.8283 19 9.5C19 8.83696 18.7366 8.20107 18.2678 7.73223C17.7989 7.26339 17.163 7 16.5 7C15.837 7 15.2011 7.26339 14.7322 7.73223C14.2634 8.20107 14 8.83696 14 9.5C14 9.8283 14.0647 10.1534 14.1903 10.4567C14.3159 10.76 14.5001 11.0356 14.7322 11.2678C15.2011 11.7366 15.837 12 16.5 12ZM9 11C9.79565 11 10.5587 10.6839 11.1213 10.1213C11.6839 9.55871 12 8.79565 12 8C12 7.20435 11.6839 6.44129 11.1213 5.87868C10.5587 5.31607 9.79565 5 9 5C8.20435 5 7.44129 5.31607 6.87868 5.87868C6.31607 6.44129 6 7.20435 6 8C6 8.79565 6.31607 9.55871 6.87868 10.1213C7.44129 10.6839 8.20435 11 9 11ZM16.5 14C14.67 14 11 14.92 11 16.75V19H22V16.75C22 14.92 18.33 14 16.5 14ZM9 13C6.67 13 2 14.17 2 16.5V19H9V16.75C9 15.9 9.33 14.41 11.37 13.28C10.5 13.1 9.66 13 9 13Z',
        },
        {
          name: 'Statistics',
          matchedRoutes: ['ContributorsStatistics'],
          svgPath1: 'M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z',
          svgPath2: 'M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z',
        },
      ]
    })

    const activeItem = ref('Dashboard')
    const isLoggingOut = ref(false)

    const scrollOrganisationsList = () => {
      const scrollOptions = {
        top: organisationsListRef.value.scrollTop + 150,
        behavior: 'smooth',
      }
      organisationsListRef.value.scrollTo(scrollOptions)
    }

    const setActiveItem = (to) => {
      const routeName = to?.name

      if (routeName) {
        const menuItem = menuItems.value.find((item) =>
          item.matchedRoutes.includes(routeName),
        )
        if (menuItem?.name) {
          activeItem.value = menuItem.name
        }
      }
    }

    const isActive = (item) => {
      return activeItem.value === item?.name
    }

    const triggerManagerMenuItem = (item = null) => {
      const primaryRoute = item?.matchedRoutes[0]
      if (route.name !== primaryRoute) {
        router.push({ name: primaryRoute })
      }
    }

    const signOut = async () => {
      isLoggingOut.value = true
      await auth.signOut()
      window.location.reload()
    }

    watch(route, () => setActiveItem(route))

    onMounted(() => {
      setActiveItem(route)
    })

    return {
      loadingAnimationOptions,
      user,
      isSuperAdmin,

      menuItems,
      activeItem,
      isActive,
      triggerManagerMenuItem,

      isLoggingOut,

      signOut,
    }
  },
})
</script>
